import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSupabaseAuth } from '@/hooks/auth';
import { useUserManagement } from '@/hooks/useUserManagement';
import { CheckCircle, XCircle, AlertTriangle, TestTube, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

interface TestResult {
  test: string;
  status: 'success' | 'error' | 'warning';
  message: string;
  details?: any;
}

const AdminStaffFunctionTester: React.FC = () => {
  const { user, userRole, hasAccess } = useSupabaseAuth();
  const { 
    users, 
    approvalRequests, 
    loading, 
    selectedUsers,
    handleUserInvite,
    handlePasswordReset,
    handleRoleUpdate,
    handleApprovalToggle,
    handleBulkApprove,
    handleBulkRoleChange
  } = useUserManagement();

  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [testing, setTesting] = useState(false);

  const addTestResult = (test: string, status: 'success' | 'error' | 'warning', message: string, details?: any) => {
    setTestResults(prev => [...prev, { test, status, message, details }]);
  };

  const runComprehensiveTests = async () => {
    setTesting(true);
    setTestResults([]);
    
    try {
      // Test 1: Authentication Status
      addTestResult(
        'Authentication Status',
        user ? 'success' : 'error',
        user ? `Authenticated as ${user.email}` : 'Not authenticated'
      );

      // Test 2: Role Verification
      addTestResult(
        'Role Verification',
        userRole ? 'success' : 'warning',
        `Current role: ${userRole || 'Unknown'}`
      );

      // Test 3: Admin Access
      const adminAccess = hasAccess('admin');
      addTestResult(
        'Admin Access Check',
        adminAccess ? 'success' : 'warning',
        adminAccess ? 'Admin access confirmed' : 'No admin access'
      );

      // Test 4: Staff Access
      const staffAccess = hasAccess('staff');
      addTestResult(
        'Staff Access Check',
        staffAccess ? 'success' : 'warning',
        staffAccess ? 'Staff access confirmed' : 'No staff access'
      );

      // Test 5: User Management Data Loading
      addTestResult(
        'User Management Data',
        !loading && users.length > 0 ? 'success' : 'warning',
        loading ? 'Still loading...' : `Loaded ${users.length} users, ${approvalRequests.length} pending approvals`
      );

      // Test 6: Admin Functions (if admin)
      if (adminAccess) {
        try {
          // Test user invite functionality
          const testUserData = {
            email: `test-${Date.now()}@example.com`,
            name: 'Test User',
            role: 'student' as const
          };
          
          // Note: We won't actually create the user, just test the function exists
          addTestResult(
            'Admin User Creation Function',
            typeof handleUserInvite === 'function' ? 'success' : 'error',
            typeof handleUserInvite === 'function' ? 'User invite function available' : 'User invite function missing'
          );

          // Test role update function
          addTestResult(
            'Admin Role Update Function',
            typeof handleRoleUpdate === 'function' ? 'success' : 'error',
            typeof handleRoleUpdate === 'function' ? 'Role update function available' : 'Role update function missing'
          );

          // Test bulk operations
          addTestResult(
            'Admin Bulk Operations',
            typeof handleBulkApprove === 'function' && typeof handleBulkRoleChange === 'function' ? 'success' : 'warning',
            typeof handleBulkApprove === 'function' && typeof handleBulkRoleChange === 'function' 
              ? 'Bulk operations available' 
              : 'Some bulk operations missing'
          );

        } catch (error) {
          addTestResult(
            'Admin Functions Test',
            'error',
            `Error testing admin functions: ${error.message}`,
            error
          );
        }
      }

      // Test 7: Staff Functions (if staff)
      if (staffAccess) {
        try {
          // Test staff user management
          addTestResult(
            'Staff User Management',
            users.length >= 0 ? 'success' : 'warning',
            `Staff can view ${users.length} users`
          );

          // Test staff approval functions
          addTestResult(
            'Staff Approval Functions',
            typeof handleApprovalToggle === 'function' ? 'success' : 'error',
            typeof handleApprovalToggle === 'function' ? 'Approval functions available' : 'Approval functions missing'
          );

        } catch (error) {
          addTestResult(
            'Staff Functions Test',
            'error',
            `Error testing staff functions: ${error.message}`,
            error
          );
        }
      }

      // Test 8: Database Connectivity
      try {
        // This test is implicit through the user data loading
        addTestResult(
          'Database Connectivity',
          'success',
          'Database connection working (users loaded successfully)'
        );
      } catch (error) {
        addTestResult(
          'Database Connectivity',
          'error',
          `Database connection issue: ${error.message}`,
          error
        );
      }

      toast.success('Function tests completed!');

    } catch (error) {
      addTestResult(
        'Test Execution',
        'error',
        `Error running tests: ${error.message}`,
        error
      );
      toast.error('Error running tests');
    } finally {
      setTesting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'border-l-green-500';
      case 'error':
        return 'border-l-red-500';
      case 'warning':
        return 'border-l-yellow-500';
      default:
        return 'border-l-gray-500';
    }
  };

  if (!hasAccess('staff')) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          You need Staff or Admin access to use the function tester.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TestTube className="h-5 w-5" />
          Admin & Staff Function Tester
          <Badge variant="secondary">{userRole}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Button 
            onClick={runComprehensiveTests} 
            disabled={testing}
            className="w-full"
          >
            {testing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {testing ? 'Running Tests...' : 'Run Comprehensive Function Tests'}
          </Button>
        </div>

        {testResults.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium">Test Results:</h4>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {testResults.map((result, index) => (
                <div 
                  key={index} 
                  className={`p-3 border-l-4 bg-muted/50 rounded-r-md ${getStatusColor(result.status)}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(result.status)}
                      <span className="font-medium text-sm">{result.test}</span>
                    </div>
                    <Badge variant={result.status === 'success' ? 'default' : result.status === 'error' ? 'destructive' : 'secondary'}>
                      {result.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{result.message}</p>
                  {result.details && (
                    <details className="mt-2">
                      <summary className="text-xs cursor-pointer">View Details</summary>
                      <pre className="text-xs mt-1 p-2 bg-muted rounded overflow-auto">
                        {JSON.stringify(result.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 p-3 bg-muted rounded-md">
              <h5 className="font-medium text-sm mb-2">Test Summary:</h5>
              <div className="flex gap-4 text-sm">
                <span className="text-green-600">
                  ✓ {testResults.filter(r => r.status === 'success').length} Passed
                </span>
                <span className="text-yellow-600">
                  ⚠ {testResults.filter(r => r.status === 'warning').length} Warnings
                </span>
                <span className="text-red-600">
                  ✗ {testResults.filter(r => r.status === 'error').length} Failed
                </span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminStaffFunctionTester;